import { ColumnDef } from '@tanstack/react-table';
import { TaskResponse } from 'services/api/task';
import { format } from 'date-fns';
import {
  faCopy,
  faEye,
  faPencil,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { Dropdown, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { ColumnActionsProps, Job, User } from '../types/types';
import { useState } from 'react';
import StatusUpdateModal from '../components/StatusUpdateModal';

const ActionCell = ({
  row,
  onEdit,
  onDuplicate,
  onDelete,
  onView
}: ColumnActionsProps) => {
  const task = row?.original;

  return (
    <div className="position-relative">
      <RevealDropdownTrigger className="position-absolute">
        <RevealDropdown>
          <>
            <Dropdown.Item
              onClick={() => onView?.((task as any)?.id || task?._id || '')}
            >
              <FontAwesomeIcon icon={faEye} className="me-2 text-primary" />
              View
            </Dropdown.Item>
            <Dropdown.Item onClick={() => onEdit(task as TaskResponse)}>
              <FontAwesomeIcon icon={faPencil} className="me-2 text-primary" />
              Edit
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() =>
                onDuplicate?.((task as any)?.id || task?._id || '')
              }
            >
              <FontAwesomeIcon icon={faCopy} className="me-2 text-primary" />
              Duplicate
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={() => onDelete((task as any)?.id || task?._id || '')}
              className="text-danger"
            >
              <FontAwesomeIcon icon={faTrash} className="me-2 text-danger" />
              Delete
            </Dropdown.Item>
          </>
        </RevealDropdown>
      </RevealDropdownTrigger>
    </div>
  );
};

const StatusCell = ({ task }: { task: TaskResponse }) => {
  const [showStatusModal, setShowStatusModal] = useState<boolean>(false);
  const status = task.status;

  const colorMap: Record<string, string> = {
    'in progress': 'info',
    completed: 'success',
    pending: 'warning',
    'not started': 'secondary'
  };

  return (
    <>
      <Badge
        bg={colorMap[status] || 'secondary'}
        pill
        onClick={() => setShowStatusModal(true)}
        style={{ cursor: 'pointer' }}
      >
        {status}
      </Badge>

      <StatusUpdateModal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        taskId={(task as any).id || task._id}
        currentStatus={status}
      />
    </>
  );
};

export const taskColumns = ({
  onEdit,
  onDelete,
  onDuplicate,
  onView
}: ColumnActionsProps): ColumnDef<TaskResponse>[] => {
  return [
    {
      accessorKey: 'task',
      header: 'Task',
      cell: ({ row }) => (
        <span className="fw-semibold text-nowrap">{row.original.task}</span>
      )
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => <span>{row.original.description || 'N/A'}</span>
    },
    {
      accessorKey: 'job',
      header: 'Job',
      cell: ({ row }) => {
        const job = (row.original as any).job || row.original.jobId;
        if (!job) {
          return <span className="text-muted">Not assigned</span>;
        }

        if (typeof job === 'object' && 'title' in job) {
          const jobObj = job as Job;
          return (
            <div className="d-flex flex-column">
              <span className="fw-semibold">{jobObj?.title}</span>
              {jobObj?.well && (
                <small className="text-muted">Well: {jobObj?.well}</small>
              )}
              {jobObj?.location && (
                <small className="text-muted">
                  Location:{' '}
                  {typeof jobObj?.location === 'string'
                    ? jobObj?.location
                    : jobObj?.location?.location || 'N/A'}
                </small>
              )}
            </div>
          );
        }

        return <span>{job.toString()}</span>;
      }
    },
    {
      accessorKey: 'assignedTo',
      header: 'Assigned To',
      cell: ({ row }) => {
        const assignedUsers = row.original.assignedTo;
        return (
          <div className="text-nowrap d-flex flex-wrap gap-1">
            {!assignedUsers || assignedUsers.length === 0 ? (
              <span className="text-muted">Unassigned</span>
            ) : (
              assignedUsers.map((user: any, index: number) => {
                let userName = 'Unknown';
                if (typeof user === 'string') {
                  userName = user;
                } else if (typeof user === 'object' && user !== null) {
                  const userObj = user as User;
                  userName =
                    `${userObj.firstName || ''} ${
                      userObj.lastName || ''
                    }`.trim() ||
                    (userObj as any).id ||
                    userObj._id;
                }
                return (
                  <Badge key={index} bg="info" className="me-1">
                    {userName}
                  </Badge>
                );
              })
            )}
          </div>
        );
      }
    },
    {
      accessorKey: 'creator',
      header: 'Creator',
      cell: ({ row }) => {
        const creator = row.original.creator;
        return (
          <span>
            {creator?.firstName || ''} {creator?.lastName || ''}
          </span>
        );
      }
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => {
        const priority = row.original.priority;
        const colorMap: Record<string, string> = {
          high: 'danger',
          medium: 'warning',
          low: 'success'
        };
        return (
          <Badge bg={colorMap[priority] || 'secondary'} pill>
            {priority}
          </Badge>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusCell task={row.original} />
    },
    {
      accessorKey: 'date',
      header: 'Due Date',
      cell: ({ row }) => {
        if (!row.original.date) return <span>—</span>;
        try {
          return (
            <span>{format(new Date(row.original.date), 'MMM dd, yyyy')}</span>
          );
        } catch (e) {
          return <span>{row.original.date}</span>;
        }
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => {
        return (
          <span>
            {format(new Date(row.original.createdAt), 'MMM dd, yyyy')}
          </span>
        );
      }
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      cell: ({ row }) => {
        return (
          <span>
            {format(new Date(row.original.updatedAt), 'MMM dd, yyyy')}
          </span>
        );
      }
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <ActionCell
          row={row}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          onView={onView}
        />
      ),
      meta: {
        headerProps: { style: { width: '80px' }, className: 'text-end' },
        cellProps: { className: 'text-end pe-3' }
      }
    }
  ];
};

export default taskColumns;
